import {
  createApiClient,
  failed,
  loading,
  ready,
  renderSnapshot,
  LOCALES,
  type ApiClient,
  type CapabilityKey,
  type CapabilitySnapshot,
  type Locale,
  type ScreenState,
} from "@planner/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { RuntimeConfig } from "./config.js";
import { platform } from "./platform/index.js";

/**
 * الجلسة — من أنا وماذا أحمل.
 *
 * **وإقلاع القوقعة نفسه يمرّ بالحالات الأربع.** لو فشل `/me` ظهرت شاشة خطأ
 * بكودها ورقم ربطها وزرّ إعادة — لا شاشة بيضاء ولا قوقعة فارغة. إن لم تكن
 * القوقعة نفسها تحترم القاعدة، فلا معنى لأن تفرضها على غيرها.
 */

export type Session = {
  readonly userId: string;
  readonly name: string;
  /** **ومعرّفُه باسم `id` كذلك** — فمكوّنُ الوجه يأخذه، ولا اسمان لشيءٍ واحد */
  readonly id: string;
  readonly mustChangePassword: boolean;
  /**
   * **ومنه تُختار القوقعة** — بنوع الحساب لا بالعنوان.
   *
   * فجلسةٌ بورتالية ترى قوقعةَ البورتال وحدها، **ويُقرَّر ذلك قبل الرسم**. وفي
   * v2 كان التحويلُ في `useEffect`، **فيُرسَم جسمُ الشاشة الخطأ إطاراً على
   * الأقل** قبل أن يقع — وميضُ شاشةٍ ليست له، يراه العميل في كل فتحة.
   */
  readonly accountKind: "internal" | "portal";
  readonly capabilities: CapabilitySnapshot;
  /**
   * **ولغةُ صاحبها من حسابه** — لا من تخزين هذا المتصفّح.
   *
   * وكانت في التخزين وحده، **فمن يدخل من تليفونٍ جديد يجد النظام بلغةٍ لم
   * يخترها**. وتجيء في نفس نداء الإقلاع، فتُطبَّق **قبل أوّل رسم**: ولو جاءت
   * في نداءٍ ثانٍ لرُسمت الشاشةُ بلغةٍ ثم انقلبت أمام عينه.
   */
  readonly email: string;
  readonly locale: Locale;
  readonly theme: "system" | "light" | "dark";
  readonly roleName: { readonly ar: string; readonly en: string } | null;
  /** **ختمُ صورته** — به يُبنى رابطُها، و`null` تعني «لا صورة» */
  readonly photoUpdatedAt: string | null;
};

type MeResponse = {
  readonly userId: string | null;
  readonly name: string | null;
  readonly mustChangePassword: boolean;
  readonly accountKind: "internal" | "portal";
  readonly capabilities: readonly CapabilityKey[];
  readonly email: string | null;
  readonly locale: Locale;
  readonly theme: "system" | "light" | "dark";
  readonly roleName: { readonly ar: string; readonly en: string } | null;
  /** **ختمُ صورته** — به يُبنى رابطُها، و`null` تعني «لا صورة» */
  readonly photoUpdatedAt: string | null;
};

export function createClient(config: RuntimeConfig, onSessionInvalid?: () => void): ApiClient {
  return createApiClient({
    baseUrl: config.apiBaseUrl,
    fetchImpl: platform.fetch,
    newCorrelationId: () => platform.newId(),
    ...(onSessionInvalid ? { onSessionInvalid } : {}),
  });
}

export function useSession(client: ApiClient): {
  readonly state: ScreenState<Session>;
  readonly reload: () => void;
} {
  const [state, setState] = useState<ScreenState<Session>>(loading);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      const result = await client.request<MeResponse>("/me", { signal: controller.signal });
      if (controller.signal.aborted) return;
      if (!result.ok) {
        setState(failed(result.error));
        return;
      }
      const me = result.data;
      setState(
        ready({
          userId: me.userId ?? "",
          id: me.userId ?? "",
          name: me.name ?? "",
          mustChangePassword: me.mustChangePassword,
          /** وافتراضُه الداخلي: جوابٌ قديمٌ من سيرفرٍ لم يُحدَّث لا يفتح بورتالاً */
          accountKind: me.accountKind === "portal" ? "portal" : "internal",
          capabilities: renderSnapshot(me.userId ?? "", me.capabilities),
          email: me.email ?? "",
          /** وافتراضُه العربية: جوابٌ من سيرفرٍ لم يُحدَّث لا يترك الشاشةَ بلا لغة */
          locale: LOCALES.find((one) => one === me.locale) ?? "ar",
          theme: me.theme === "light" || me.theme === "dark" ? me.theme : "system",
          roleName: me.roleName ?? null,
          photoUpdatedAt: me.photoUpdatedAt ?? null,
        }),
      );
    })();
    return () => {
      controller.abort();
    };
  }, [client, attempt]);

  const reload = useCallback(() => {
    setState(loading);
    setAttempt((n) => n + 1);
  }, []);

  return useMemo(() => ({ state, reload }), [state, reload]);
}

/**
 * **الخروج — تطبيقٌ واحد، تناديه القوقعةُ وشاشةُ تغيير الكلمة.**
 *
 * وكان مكتوباً سطراً داخل شاشةٍ واحدة، **ولا زرَّ له في القوقعة أصلاً**: فلا
 * الموظفُ يستطيع الخروج ولا العميل. ومسارُه موجودٌ من المرحلة الأولى، ومفتاحُ
 * نصّه في القاموس من المرحلة الأولى — **والزرُّ لم يُرسَم قط**.
 *
 * ولا يُرمى من هنا: فشلُ الخروج يُعاد لمن ناداه ليقول رسالته، **والجلسةُ
 * تُعاد قراءتها على أي حال** — فإن كانت قد انتهت فعلاً، ظهرت شاشةُ الدخول.
 */
export async function signOut(client: ApiClient): Promise<void> {
  await client.request("/auth/session", { method: "DELETE" });
}
